import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  QrCode, ShieldCheck, BookOpenCheck, ArrowRight, Link2, Gift, CalendarDays,
  ArrowLeftRight, Smartphone, Sparkles, Zap, Coins, Users, TrendingUp, Lock,
  CheckCircle2, Headphones, ArrowDownToLine, ArrowUpFromLine,
} from "lucide-react";
import { publicApi } from "@/lib/api";
import { fmtMoney, fmtDate, daysLeft } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TiltCard, VaultSparkles } from "@/components/vault";
import { cn } from "@/lib/utils";

const fade = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };

/* ---------- 3D hero coin (real logo image, mouse-reactive) ---------- */
function HeroCoin() {
  const [t, setT] = useState({ rx: 0, ry: 0 });
  const [floatY, setFloatY] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    let raf;
    let start = performance.now();
    const loop = (now) => {
      setFloatY(Math.sin((now - start) / 1600) * 10);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ rx: -py * 25, ry: px * 25 });
  };
  const reset = () => setT({ rx: 0, ry: 0 });

  return (
    <div
      className="relative mx-auto flex h-72 w-72 items-center justify-center sm:h-96 sm:w-96"
      style={{ perspective: 1200 }}
      onMouseMove={onMove}
      onMouseLeave={reset}
      ref={ref}
      data-testid="landing-hero-coin"
    >
      {/* Glow */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.45) 0%, transparent 60%)" }}
      />
      {/* Floating badge coins */}
      <motion.div
        className="absolute left-2 top-8 hidden h-16 w-16 rounded-full shadow-xl sm:block"
        animate={{ y: [0, -16, 0] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src="/brand/logo.png" alt="" className="h-full w-full object-contain opacity-80" />
      </motion.div>
      <motion.div
        className="absolute bottom-6 right-0 hidden h-12 w-12 rounded-full shadow-lg sm:block"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src="/brand/logo.png" alt="" className="h-full w-full object-contain opacity-70" />
      </motion.div>
      {/* The big coin */}
      <motion.div
        className="relative h-56 w-56 sm:h-80 sm:w-80"
        style={{
          transform: `translateY(${floatY}px) rotateX(${t.rx}deg) rotateY(${t.ry}deg)`,
          transformStyle: "preserve-3d",
          transition: "transform 160ms ease-out",
        }}
      >
        <img
          src="/brand/logo.png"
          alt="RupeeTether coin"
          className="h-full w-full rounded-full object-contain"
          style={{ filter: "drop-shadow(0 20px 40px hsl(44 86% 30% / 0.5))" }}
        />
      </motion.div>
    </div>
  );
}

/* ---------- Scrolling ticker ---------- */
function MarqueeTicker({ rates }) {
  const items = [
    `BUY 1 USDT = ${rates ? fmtMoney(rates.buy_rate, "INR") : "..."}`,
    `SELL 1 USDT = ${rates ? fmtMoney(rates.sell_rate, "INR") : "..."}`,
    `FEE ${rates?.fee_pct ?? 0}%`,
    "VERIFIED BY HUMANS",
    "₹100 PER REFERRAL",
    "LIFETIME 5% COMMISSION",
    "DEPOSITS AUTO-VERIFIED EVERY 15 MIN",
  ];
  const row = [...items, ...items];
  return (
    <div
      aria-hidden
      className="relative flex overflow-hidden border-y border-amber-200/60 bg-gradient-to-r from-slate-950 via-[#111c3b] to-slate-950 py-3"
    >
      <div
        className="flex min-w-max shrink-0 animate-[marquee_30s_linear_infinite] items-center gap-10 pr-10 text-sm font-semibold uppercase tracking-wider text-amber-300"
      >
        {row.map((text, i) => (
          <span key={i} className="num inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" />
            {text}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["public-overview"],
    queryFn: () => publicApi.get("/public/overview").then((r) => r.data),
    refetchInterval: 60000,
  });
  const rates = data?.rates;
  const inr = data?.bonuses?.find((b) => b.currency === "INR");
  const usdt = data?.bonuses?.find((b) => b.currency === "USDT");

  const { scrollYProgress } = useScroll();
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -200]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* --- Top promise banner --- */}
      <div
        className="relative overflow-hidden border-b border-amber-400/20 bg-gradient-to-r from-amber-500/15 via-amber-400/25 to-amber-500/15 text-amber-100"
        data-testid="promise-banner"
        role="status"
      >
        <div className="pointer-events-none absolute inset-0 opacity-40 [background:linear-gradient(90deg,transparent_0%,hsl(44_100%_80%/0.25)_50%,transparent_100%)] animate-[promise-shine_6s_linear_infinite]" />
        <div className="relative mx-auto flex max-w-6xl items-center justify-center gap-2 px-4 py-2 text-center text-xs font-medium tracking-wide sm:text-sm">
          <ShieldCheck className="h-4 w-4 shrink-0 text-amber-300" />
          <span className="num font-semibold text-white">Every payment verified within 15 minutes</span>
          <span className="hidden text-amber-200/80 sm:inline">· backed by our team, every day</span>
        </div>
        <style>{`@keyframes promise-shine{0%{transform:translateX(-100%);}100%{transform:translateX(100%);}}`}</style>
      </div>

      {/* --- Nav --- */}
      <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="landing-logo-link"><Logo dark size={34} /></Link>
          <nav className="ml-10 hidden gap-6 text-sm text-slate-300 md:flex">
            <a href="#how" className="hover:text-white" data-testid="landing-nav-how">How it works</a>
            <a href="#faq" className="hover:text-white" data-testid="landing-nav-faq">FAQ</a>
          </nav>
          <div className="ml-auto flex gap-2">
            {user ? (
              <Button asChild className="btn-lift bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 hover:brightness-105" data-testid="landing-dashboard-button">
                <Link to="/dashboard">Open dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 hover:text-white" data-testid="landing-signin-button">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild className="btn-lift bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 hover:brightness-105" data-testid="landing-register-button">
                  <Link to="/register">Create account</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* --- Hero --- */}
      <section className="relative overflow-hidden">
        {/* 3D ambient aurora */}
        <motion.div
          aria-hidden
          style={{ y: parallaxY }}
          className="pointer-events-none absolute inset-0"
        >
          <div className="absolute -left-20 top-10 h-96 w-96 rounded-full"
               style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.25) 0%, transparent 65%)" }} />
          <div className="absolute right-0 top-32 h-96 w-96 rounded-full"
               style={{ background: "radial-gradient(circle, hsl(145 70% 48% / 0.2) 0%, transparent 65%)" }} />
          <div className="absolute bottom-0 left-1/3 h-[520px] w-[520px] rounded-full"
               style={{ background: "radial-gradient(circle, hsl(186 72% 48% / 0.2) 0%, transparent 65%)" }} />
        </motion.div>
        <VaultSparkles count={20} />

        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 pt-14 pb-20 sm:px-6 md:py-24 lg:grid-cols-12 lg:px-8">
          <motion.div {...fade} className="lg:col-span-7">
            <div
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-medium text-amber-200"
              data-testid="live-rate-pill"
            >
              <span className="pulse-soft h-2 w-2 rounded-full bg-amber-400" />
              1 USDT = <span className="num font-semibold text-white" data-testid="live-rate-inr-usdt">{rates ? fmtMoney(rates.buy_rate, "INR") : "..."}</span>
              <span className="text-slate-400">· updated {rates ? fmtDate(rates.updated_at) : "—"}</span>
            </div>
            <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              INR <span className="inline-block"><span className="bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent">↔</span></span>{" "}
              <span className="bg-gradient-to-br from-emerald-200 via-emerald-400 to-emerald-600 bg-clip-text text-transparent">USDT</span>,
              <br />
              <span className="text-white">team-verified</span> &amp; lightning fast.
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-300 md:text-lg">
              Deposit rupees by UPI or payment link, swap between INR and USDT at transparent, admin-set rates, and have every payment reviewed by our team.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="btn-lift gap-2 bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 text-slate-950 shadow-[0_14px_36px_hsl(44_86%_50%/0.35)] hover:brightness-105"
                data-testid="hero-cta-register"
              >
                <Link to={user ? "/deposit" : "/register"}>
                  {user ? "Make a deposit" : "Create free account"} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              {!user && (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="btn-lift border-amber-400/40 bg-white/5 text-white hover:bg-white/10"
                  data-testid="hero-cta-signin"
                >
                  <Link to="/login">Sign in</Link>
                </Button>
              )}
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
              <span className="inline-flex items-center gap-1.5"><QrCode className="h-4 w-4 text-amber-400" /> UPI QR</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Manual review</span>
              <span className="inline-flex items-center gap-1.5"><BookOpenCheck className="h-4 w-4 text-amber-400" /> Ledger entries</span>
              <span className="inline-flex items-center gap-1.5"><Headphones className="h-4 w-4 text-emerald-400" /> Dedicated team support</span>
            </div>
          </motion.div>

          <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }} className="lg:col-span-5">
            <HeroCoin />
          </motion.div>
        </div>

        <MarqueeTicker rates={rates} />
      </section>

      {/* --- Rate cards --- */}
      <section className="relative mx-auto -mt-10 max-w-6xl px-4 sm:px-6 lg:px-8">
        <TiltCard className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 shadow-[0_20px_60px_hsl(222_60%_12%/0.5)] ring-1 ring-amber-400/20" strength={6}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <RateTile label="You buy USDT at" value={rates?.buy_rate} currency="INR" accent="amber" testId="landing-buy-rate" />
            <RateTile label="You sell USDT at" value={rates?.sell_rate} currency="INR" accent="emerald" testId="landing-sell-rate" />
            <RateTile label="Platform fee" value={null} display={`${rates?.fee_pct ?? 0}%`} accent="slate" />
          </div>
        </TiltCard>
      </section>

      {/* --- How to deposit + How to withdraw --- */}
      <section id="how" className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Deposits & withdrawals"
          title={<>Two simple flows — <span className="text-amber-400">no fine print.</span></>}
          sub="Follow the steps on the left to add money, or the steps on the right to cash out."
        />
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {/* Deposit column */}
          <TiltCard
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-[#111c3b] p-6 ring-1 ring-emerald-400/20 sm:p-8"
            strength={6}
            testId="landing-how-deposit"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full"
              style={{ background: "radial-gradient(circle, hsl(145 70% 48% / 0.3) 0%, transparent 65%)" }}
            />
            <div className="relative flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-400/15 text-emerald-300">
                <ArrowDownToLine className="h-6 w-6" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">How to deposit</p>
                <p className="font-display text-2xl font-semibold text-white">Add money to your wallet</p>
              </div>
            </div>
            <ol className="relative mt-8 space-y-6">
              {[
                { t: "Sign in and open Deposit", d: "From your dashboard, hit Deposit and pick INR or USDT." },
                { t: "Choose a payment method", d: "Pay via UPI QR, UPI handle, or the secure payment link we display." },
                { t: "Enter the UTR / reference", d: "Share the transaction reference and optionally upload the receipt." },
                { t: "Our team approves", d: "Our team verifies every payment within 15 minutes. Deposit and bonus arrive as separate ledger entries." },
              ].map((s, i) => (
                <li key={s.t} className="flex gap-4" data-testid={`landing-deposit-step-${i + 1}`}>
                  <span className="num flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-400/15 text-sm font-bold text-emerald-200">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-semibold text-white">{s.t}</p>
                    <p className="mt-0.5 text-sm text-slate-300">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Button
              asChild
              size="lg"
              className="btn-lift mt-8 w-full gap-2 bg-gradient-to-br from-emerald-400 to-emerald-600 text-slate-950 hover:brightness-105 sm:w-auto"
              data-testid="landing-deposit-cta"
            >
              <Link to={user ? "/deposit" : "/register"}>
                {user ? "Make a deposit" : "Start depositing"} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </TiltCard>

          {/* Withdrawal column */}
          <TiltCard
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-[#1a1220] p-6 ring-1 ring-amber-400/20 sm:p-8"
            strength={6}
            testId="landing-how-withdraw"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full"
              style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.35) 0%, transparent 65%)" }}
            />
            <div className="relative flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-400/15 text-amber-300">
                <ArrowUpFromLine className="h-6 w-6" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">How to withdraw</p>
                <p className="font-display text-2xl font-semibold text-white">Cash out to UPI or bank</p>
              </div>
            </div>
            <ol className="relative mt-8 space-y-6">
              {[
                { t: "Open the Trade vault", d: "Head to Trade and switch to the Sell USDT tab." },
                { t: "Enter the USDT amount", d: "We lock the price at admin-set rate the moment you confirm." },
                { t: "Add your payout detail", d: "UPI ID, bank account + IFSC, or an IMPS/RTGS note — your pick." },
                { t: "Our team pays you out", d: "INR lands in your account within 15 minutes of verification; you'll get a notification with the UTR / payment reference." },
              ].map((s, i) => (
                <li key={s.t} className="flex gap-4" data-testid={`landing-withdraw-step-${i + 1}`}>
                  <span className="num flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-400/15 text-sm font-bold text-amber-200">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-semibold text-white">{s.t}</p>
                    <p className="mt-0.5 text-sm text-slate-300">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Button
              asChild
              size="lg"
              className="btn-lift mt-8 w-full gap-2 bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 hover:brightness-105 sm:w-auto"
              data-testid="landing-withdraw-cta"
            >
              <Link to={user ? "/trade" : "/register"}>
                {user ? "Sell USDT" : "Start withdrawing"} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </TiltCard>
        </div>
      </section>

      {/* --- Mega CTA with 3D coin --- */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(900px circle at 50% 110%, hsl(44 86% 50% / 0.3) 0%, transparent 55%)",
          }}
        />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-20 sm:px-6 md:grid-cols-2 lg:px-8">
          <div>
            <h2 className="font-display text-3xl font-semibold sm:text-4xl">
              Ready to <span className="bg-gradient-to-br from-amber-200 to-amber-500 bg-clip-text text-transparent">put your rupees</span> to work?
            </h2>
            <p className="mt-3 max-w-md text-slate-300">
              Create a free account in under a minute. We'll give you an INR wallet, a USDT wallet, and your first deposit bonus.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="btn-lift bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 shadow-[0_14px_36px_hsl(44_86%_50%/0.35)] hover:brightness-105"
                data-testid="cta-register-primary"
              >
                <Link to={user ? "/trade" : "/register"}>
                  {user ? "Open the trade vault" : "Create free account"} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
            <ul className="mt-6 grid gap-2 text-sm text-slate-300">
              {[
                "No KYC up front — start exploring immediately.",
                "Every deposit matched to a real payment.",
                "Instant INR ↔ USDT swap between your wallets.",
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex items-center justify-center">
            <HeroCoin />
          </div>
        </div>
      </section>

      {/* --- FAQ --- */}
      <section id="faq" className="mx-auto max-w-3xl px-4 pb-20 sm:px-6">
        <SectionHeading
          eyebrow="FAQ"
          title={<>Short answers to <span className="text-amber-400">common questions.</span></>}
          sub="If something isn't covered here, our Telegram team can help."
        />
        <Accordion type="single" collapsible className="mt-6">
          {[
            ["How long does verification take?", "Our team verifies every payment within 15 minutes during active hours."],
            ["Is a screenshot enough to get credited?", "No. Receipts help our team, but every deposit is matched against the actual payment record before approval."],
            ["Can a deposit be credited twice?", "No. Each deposit has a unique request ID and transaction reference, and our ledger refuses duplicate credits."],
            ["When is the bonus applied?", "The bonus is calculated when you submit and credited once, as a separate ledger entry, after your deposit is approved."],
            ["My payment is debited but not credited — what do I do?", "Open the deposit from your transactions, hit Raise a dispute, and our team will contact you on your phone or Telegram within 2 hours."],
          ].map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`} className="border-slate-800">
              <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left text-white hover:text-amber-200">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-300">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* --- Footer --- */}
      <footer className="border-t border-slate-800 bg-slate-950/60">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-10 text-sm text-slate-400 sm:flex-row sm:items-center sm:px-6 lg:px-8">
          <Logo dark size={30} />
          <p className="sm:ml-6">Crypto assets are volatile. Deposits are credited only after verification.</p>
          <Link to="/admin/login" className="hover:text-white sm:ml-auto" data-testid="footer-admin-link">
            Admin
          </Link>
        </div>
      </footer>

      {/* marquee keyframes */}
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

function SectionHeading({ eyebrow, title, sub }) {
  return (
    <div className="max-w-2xl">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-200">
        <Sparkles className="h-3 w-3" /> {eyebrow}
      </span>
      <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
      {sub && <p className="mt-3 text-base text-slate-300">{sub}</p>}
    </div>
  );
}

function RateTile({ label, value, display, currency = "INR", accent = "amber", testId }) {
  const toneMap = {
    amber: "ring-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 text-amber-200",
    emerald: "ring-emerald-400/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 text-emerald-200",
    slate: "ring-white/10 bg-white/5 text-slate-200",
  };
  return (
    <div className={cn("relative overflow-hidden rounded-xl p-5 ring-1", toneMap[accent])}>
      <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
      <p className="num mt-2 text-3xl font-bold text-white sm:text-4xl" data-testid={testId}>
        {display ?? (value != null ? fmtMoney(value, currency) : "—")}
      </p>
    </div>
  );
}
